
import React from "react";
import { ReagentRequest } from "@/types/reagentRequest.types";
import ReagentRequestCard from "./ReagentRequestCard";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ReagentRequestListProps {
  requests: ReagentRequest[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  resetFilters: () => void;
  onEditRequest?: (request: ReagentRequest) => void;
}

const ReagentRequestList: React.FC<ReagentRequestListProps> = ({
  requests,
  isLoading,
  error,
  refetch,
  resetFilters,
  onEditRequest
}) => {
  if (isLoading) {
    return (
      <div className="col-span-3 flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="col-span-3 flex flex-col items-center justify-center min-h-[400px] space-y-4 text-center">
        <h3 className="text-xl font-semibold">Error loading reagent requests</h3>
        <p className="text-muted-foreground">{error.message || "An unknown error occurred"}</p>
        <Button onClick={refetch}>Try Again</Button>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="col-span-3 flex flex-col items-center justify-center min-h-[400px] space-y-4 text-center">
        <h3 className="text-xl font-semibold">No reagent requests found</h3>
        <p className="text-muted-foreground">No reagent requests match your current filters or there are no active requests.</p>
        <Button onClick={resetFilters} variant="outline">Reset Filters</Button>
      </div>
    );
  }

  return (
    <div className="lg:col-span-3 grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      {requests.map((request) => (
        <ReagentRequestCard
          key={request.id}
          request={request}
          onUpdate={refetch}
          onEdit={onEditRequest}
        />
      ))}
    </div>
  );
};

export default ReagentRequestList;
